import { hasAccess } from "$root/components/AuthorizationGuard";
import { typedUrlForRoute, useTypedNavigation } from "$root/components/PlatformRouter/RoutesDef";
import type { DefaultSelectionProps } from "$root/pages/PortfoliosStudio/PortfolioList";
import { useBulkAnalysisActions } from "$root/pages/PortfoliosStudio/PortfolioList/portfolio-actions";
import type { IconName } from "@mdotm/shadcn/legacy/components";
import { type BulkActionItem, BulkActions } from "@mdotm/shadcn/mdotm/components";
import { Map, Set } from "immutable";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CompareOverlay, type CompareOverlayProps, generateCompareItem } from "../compare-portfolio/CompareOverlay";
import { PortfolioStudioSettingTabEnum } from "../portfolio-studio-settings/tab-enum";
import { useCanSendToExecution } from "../portfolio/execution";
import { portfolioRepository } from "../portfolio/repository";
import { exportTemplateIcon, exportTemplateLabel } from "../reports/export-templates";
import { reportRepository } from "../reports/repository";
import { storyfolioRepository } from "../storyfolio/repository";
import { useUserValue } from "../user";
import { buildDataRequest, selectSort, useWorkspaceStore } from "./state";
import { workspaceRepository } from "./repository";
import { filterMap } from "$root/utils";
import { useAtomValue } from "@mdotm/shadcn/legacy/reactivity";

export function WorkspacePortfolioBulkActions({
	uuidsInPage,
	onSelect,
	selection,
}: {
	/**
	 * The uuids of every row on the page the caller is showing, or undefined where the caller has no page
	 * (the chart), which is what drops "Select all in page" from the menu. It has to be the whole page: a
	 * caller that hands over only the rows it has rendered makes the action select a subset.
	 */
	uuidsInPage?: string[];
	selection: string[];
	onSelect(newSelection: string[], selectionType: "partial" | "page" | "all"): void;
}) {
	const { t } = useTranslation();
	const { push } = useTypedNavigation();
	const user = useUserValue();
	const { dispatch, slices, readState } = useWorkspaceStore();
	const { data: defaultWorkspace } = workspaceRepository.getDefaultWorkspace.useQuery();
	const canSendToExecution = useCanSendToExecution();
	const upstreamWorkspace = useAtomValue(slices.document$);

	const unfilteredProjectedPortfolios = portfolioRepository.projectedList.useQuery().data ?? [];
	const unfilteredProjectedRowsMap = useMemo(
		() => Map(unfilteredProjectedPortfolios.filter((x) => x.uuid).map((x) => [x.uuid!, x])),
		[unfilteredProjectedPortfolios],
	);

	const selectedNodesAsPtf = useMemo(
		() =>
			unfilteredProjectedRowsMap
				? selection.flatMap((uuid) => {
						const candidate = unfilteredProjectedRowsMap.get(uuid);
						if (candidate) {
							return [candidate];
						}
						return [];
					})
				: [],
		[unfilteredProjectedRowsMap, selection],
	);

	const queryExportTemplates = reportRepository.exportTemplates.useQuery();
	const exportTemplates = queryExportTemplates.data ?? [];
	const queryCommentaryTemplates = storyfolioRepository.templates.useQuery();

	const commentaryTemplateOptions = useMemo(() => {
		const commentaryTemplates = queryCommentaryTemplates.data;
		if (!commentaryTemplates) {
			return [];
		}
		return commentaryTemplates.flatMap((x) =>
			x.visible && x.uuid && x.name ? [{ label: x.name, value: x.uuid }] : [],
		);
	}, [queryCommentaryTemplates]);

	const [comparedInvestments, setComparedInvestments] = useState<CompareOverlayProps["compareData"]>([]);
	const onCompareMultipleInvestments = useCallback(
		(selected?: Map<string, DefaultSelectionProps>) => {
			setComparedInvestments((prev) => {
				const selectedPortfolios = Set<string>([...prev.map((x) => x.uuid ?? ""), ...(selected?.keys() ?? [])]);

				return selectedPortfolios.toArray().flatMap((uuid) => {
					const investment = unfilteredProjectedRowsMap?.get(uuid);
					if (!investment) {
						return [];
					}

					const preferences = selected?.get(uuid);
					const existingPortfolio = prev.filter((portfolio) => portfolio.uuid === uuid);
					const currentPortfolio = generateCompareItem(investment, false);
					const enhancedPortfolio = generateCompareItem(investment, true);
					if (existingPortfolio.length === 0) {
						return preferences === undefined
							? [currentPortfolio]
							: [
									...(preferences.current ? [currentPortfolio] : []),
									...(preferences.enhance ? [enhancedPortfolio] : []),
								];
					}

					return preferences === undefined
						? existingPortfolio
						: [
								...(preferences.current ? [existingPortfolio.find((x) => !x.enhanced) ?? currentPortfolio] : []),
								...(preferences.enhance ? [existingPortfolio.find((x) => x.enhanced) ?? enhancedPortfolio] : []),
							];
				});
			});
		},
		[unfilteredProjectedRowsMap],
	);

	const portfolioBulkActions = useBulkAnalysisActions(selectedNodesAsPtf, {
		currentlyComparedInvestments: comparedInvestments.map(({ uuid }) => uuid!),
		setManualSelection: (newSelection) => onSelect(newSelection, "partial"),
		setCompareMultipleInvestments: onCompareMultipleInvestments,
		resetMultiselectCtx: () => onSelect([], "all"),
		onOpenNewTab(investmentsUuids) {
			if (!upstreamWorkspace?.uuid || upstreamWorkspace?.uuid === defaultWorkspace?.uuid) {
				return typedUrlForRoute("PortfolioStudio/DefaultWorkspace", {
					preselectedInvestments: investmentsUuids.join("|"),
				});
			}
			return typedUrlForRoute("PortfolioStudio/Workspace", {
				id: upstreamWorkspace.uuid,
				preselectedInvestments: investmentsUuids.join("|"),
			});
		},
		workspaceUid: upstreamWorkspace?.uuid,
	});

	const actions = useMemo<BulkActionItem[][]>(
		() => [
			[
				{
					label: "Manage Selection",
					"data-qualifier": "PortfolioStudio/PortfolioList/BatchActionSubmenu(ManageSelection)",
					dropdownItems: [
						// {
						// 	label: "Select all",
						// 	onClick: ()=>onSelect(),
						// 	"data-qualifier": "PortfolioStudio/PortfolioList/BatchAction(ApplyFilter)",
						// } satisfies BulkActionItem,
						{
							label: "Select all",
							async onClickAsync() {
								// Every portfolio the workspace is currently filtered to, which is the same predicate the table
								// and the charts ask against — hence the shared selector rather than a fourth hand-assembly.
								// It projects nothing and reads uuids off `predefinedFields`, so the pool it carries is only
								// what the predicate and the sort name; overriding it would send a predicate whose concepts
								// are absent from the request.
								const state = readState();
								const sort = selectSort(state);
								const result = await workspaceRepository.getWorkspaceData.fetchQuery(
									buildDataRequest(state, {
										projection: [],
										extras: { limit: 1000, offset: 0, sorting: sort ? [sort] : undefined },
									}),
								);
								const validInvestmentsUuid =
									result.items?.flatMap((item): [string] | [] =>
										item.predefinedFields?.uuid ? [item.predefinedFields?.uuid] : [],
									) ?? [];
								onSelect(validInvestmentsUuid, "all");
							},
							"data-qualifier": "PortfolioStudio/PortfolioList/BatchAction(SelectAll)",
						} satisfies BulkActionItem,
						...(uuidsInPage
							? [
									{
										label: "Select all in page",
										onClick: () => onSelect(uuidsInPage, "page"),
										disabled: uuidsInPage.length === 0,
										"data-qualifier": "PortfolioStudio/PortfolioList/BatchAction(ApplyFilter)",
									} satisfies BulkActionItem,
								]
							: []),
						{
							label: "Unselect",
							onClick: () => onSelect([], "all"),
							"data-qualifier": "PortfolioStudio/PortfolioList/BatchAction(Unselect)",
						} satisfies BulkActionItem,
						{
							label: "Filter selection",
							onClick: () => {
								void dispatch({
									type: "upsertFilter",
									chain: {
										name: "Filter selection",
										comparisonMode: "in",
										comparisonValue: [selection],
										concept: {
											id: "systemId: name",
											dataType: "SPHERE_ENTITY",
										},
										source: "CURRENT",
										timeHorizon: undefined,
									},
								});
								onSelect([], "all");
							},
							"data-qualifier": "PortfolioStudio/PortfolioList/BatchAction(ApplyFilter)",
						} satisfies BulkActionItem,
					],
				},
			],
			[
				{
					label: "Manage Portfolio",
					"data-qualifier": "PortfolioStudio/PortfolioList/BatchActionSubmenu(ManagePortfolio)",
					dropdownItems: [
						// delete
						{
							label: "Delete",
							icon: "Delete" satisfies IconName,
							onClick: () => portfolioBulkActions.delete(),
							"data-qualifier": "PortfolioStudio/PortfolioList/BatchAction(Delete)",
						},
						//edit portfolio settings
						{
							label: "Edit market view",
							icon: "Settings" satisfies IconName,
							onClickAsync: () => portfolioBulkActions.editSettings(),
							"data-qualifier": "PortfolioStudio/PortfolioList/BatchAction(EditPortfolioSettings)",
						},

						// merge (aka: create new portfolio with editor)
						{
							label: "Merge",
							icon: "merge" satisfies IconName,
							// biome-ignore lint/correctness/useHookAtTopLevel: not a hook
							onClick: () => portfolioBulkActions.useAsMixedPortfolio(),
							"data-qualifier": "PortfolioStudio/PortfolioList/BatchAction(MergeComposition)",
						},

						// compare
						{
							label: "Compare",
							icon: "compare" satisfies IconName,
							onClick: () => portfolioBulkActions.compare(),
							"data-qualifier": "PortfolioStudio/PortfolioList/BatchAction(Compare)",
						},

						// custom attributes (TBD)
						// {
						// 	label: "Apply attribute",
						// 	icon: "compare" satisfies IconName,
						// 	onClick: () => portfolioBulkActions.compare(),
						// 	"data-qualifier": "PortfolioStudio/PortfolioList/BatchAction(Compare)",
						// },
					],
				},

				{
					label: "Proposal Workflow",
					"data-qualifier": "PortfolioStudio/PortfolioList/BatchActionSubmenu(ProposalWorkflow)",
					dropdownItems: [
						//create proposal
						{
							label: "Create proposal",
							icon: "Enhance" satisfies IconName,
							onClickAsync: () => portfolioBulkActions.createProposal(),
							"data-qualifier": "PortfolioStudio/PortfolioList/BatchAction(CreateProposal)",
						},
						{
							label: "Accept proposal",
							icon: "Outline" satisfies IconName,
							"data-qualifier": "PortfolioStudio/PortfolioList/BatchActionSubmenu(AcceptProposal)",
							dropdownItems: [
								{
									label: "Accept proposal",
									onClickAsync: () => portfolioBulkActions.acceptProposal(),
									"data-qualifier": "PortfolioStudio/PortfolioList/BatchAction(AcceptProposal)",
								},
								{
									label: "Accept in the past",
									onClickAsync: () => portfolioBulkActions.acceptProposalInThePast(),
									"data-qualifier": "PortfolioStudio/PortfolioList/BatchAction(AcceptProposalInThePast)",
								},
							],
						},
						{
							label: "Reject proposal",
							icon: "Close" satisfies IconName,
							onClickAsync: () => portfolioBulkActions.rejectProposal(),
							"data-qualifier": "PortfolioStudio/PortfolioList/BatchAction(RejectProposal)",
						},
						// Only where Sphere has somewhere to send an executed proposal — the same gate the
						// single-portfolio Execute and Stop execution buttons use. Both flips of that switch sit
						// last so that accept and reject stay side by side.
						...(canSendToExecution
							? [
									{
										label: "Send to execution",
										icon: "Send-outline" satisfies IconName,
										onClickAsync: () => portfolioBulkActions.sendToExecution(),
										"data-qualifier": "PortfolioStudio/PortfolioList/BatchAction(SendToExecution)",
									} satisfies BulkActionItem,
									{
										label: "Stop execution",
										icon: "Cancel" satisfies IconName,
										onClickAsync: () => portfolioBulkActions.stopExecution(),
										"data-qualifier": "PortfolioStudio/PortfolioList/BatchAction(StopExecution)",
									} satisfies BulkActionItem,
								]
							: []),
					],
				},

				{
					label: "Reporting",
					"data-qualifier": "PortfolioStudio/PortfolioList/BatchActionSubmenu(Reporting)",
					dropdownItems: [
						// download
						{
							label: "Export report",
							icon: "Dowload" satisfies IconName,
							dropdownItems: [
								...filterMap(exportTemplates, (template) =>
									!template.visible
										? []
										: [
												{
													icon: exportTemplateIcon(template),
													label: exportTemplateLabel(template),
													onClickAsync: () => portfolioBulkActions.downloadReport(template),
													"data-qualifier": `PortfolioStudio/PortfolioList/BatchAction(Download)/Report(${template.templateName})`,
												} satisfies BulkActionItem,
											],
								),
								{
									icon: "xls" satisfies IconName,
									label: "Export composition",
									onClickAsync: () => portfolioBulkActions.exportComposition(),
									"data-qualifier": "PortfolioStudio/PortfolioList/BatchAction(Download)/ExportComposition",
								} satisfies BulkActionItem,
								...(hasAccess(user, { requiredService: "EXPORT" })
									? (user.exportFormats ?? []).map(
											(format) =>
												({
													icon: "xls",
													onClickAsync: () => portfolioBulkActions.downloadComposition(format),
													label: `${t(`EXPORT.${format}`)} template`,
													"data-qualifier": `PortfolioStudio/PortfolioList/BatchAction(Download)/Composition(${format})`,
												}) satisfies BulkActionItem,
										)
									: []),
								...(hasAccess(user, { requiredService: "INVESTMENTS_REPORT_TEMPLATE_EDITOR" })
									? [
											{
												icon: "Settings" satisfies IconName,
												label: "Report builder",
												"data-qualifier": "PortfolioStudio/PortfolioList/BatchAction(Download)/ReportBuilder",
												onClick: () =>
													push("PortfolioStudioSettings", {
														tab: PortfolioStudioSettingTabEnum.ReportCustomisation,
													}),
											} satisfies BulkActionItem,
										]
									: []),
							],
							"data-qualifier": "PortfolioStudio/PortfolioList/BatchAction(Download)",
						},
						//generate commentary
						{
							label: "Generate commentary",
							"data-qualifier": "PortfolioStudio/PortfolioList/BatchActionSubmenu(GenerateCommentary)",
							icon: "sphere-ai" satisfies IconName,
							dropdownItems: commentaryTemplateOptions.map(
								(commentaryTemplate) =>
									({
										label: commentaryTemplate.label,
										"data-qualifier": `PortfolioStudio/PortfolioList/BatchAction(GenerateCommentary)/${commentaryTemplate.label}`,
										onClickAsync: () => portfolioBulkActions.generateCommentary(commentaryTemplate.value),
									}) satisfies BulkActionItem,
							),
						},
					],
				},
				{
					label: "Share",
					icon: "share",
					onClickAsync: () => portfolioBulkActions.share(),
				},
			],
		],
		[
			user,
			exportTemplates,
			commentaryTemplateOptions,
			canSendToExecution,
			t,
			portfolioBulkActions,
			push,
			onSelect,
			uuidsInPage,
			selection,
			dispatch,
			readState,
		],
	);

	return (
		<>
			<BulkActions variant="floating" count={selection.length} actions={actions} onClear={() => onSelect([], "all")} />
			<CompareOverlay
				show={comparedInvestments.length > 0}
				onClose={() => setComparedInvestments([])}
				onRemove={(id) => setComparedInvestments((v) => v.filter((x) => x.id !== id))}
				compareData={comparedInvestments}
			/>
		</>
	);
}
